import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Select, Tag } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { getRandomColor, getTagColor } from '../../../../js/helper';
import usePrevious from  '../../../../js/usePrevious';
import { addWorkspaceTag } from '../../../../actions/WorkspaceActions';
import { toggleWorkspaceTagsModal } from '../../../../actions/modalAction';
import { compact, isEmpty, isEqual } from 'lodash';

const TaskTagsSection = ({ workspace, updateTaskTags, tags }) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const [taskTags, setTaskTags] = useState([]);
  const [workspaceTags, setWorkspaceTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const prevTags = usePrevious(tags);

  const wTags = useSelector((state) => state.workspaceReducer.workspaceTags);
  const role = useSelector((state) => state.userReducer.role);
  const isOwnerOrAdmin = role === 'owner' || role === 'admin';

  useEffect(() => {
    setWorkspaceTags(wTags.map((tag, i) => ({
      value: tag?.tag,
      label: tag?.tag,
      color: tag?.metadata?.color || getRandomColor(i),
      id: tag?._id
    })));
  }, [wTags])

  useEffect(() => {
    if (prevTags === tags) {
      return;
    }

    const newTags = compact(tags.map(tag => tag?.tag));

    if (isEmpty(newTags) && tags?.length > 0) {
      // Invalid data format. BE sometimes sends the array of tag ids instead of tag entities.
      // TODO: handle the above case with schema. Always provide ids in the component. Each tag should be selected from store individually
      return;
    }

    if (isEqual(taskTags?.sort(), newTags?.sort())) {
      return;
    }

    setTaskTags(tags.map(tag => tag?.tag));
  }, [prevTags, tags, taskTags])

  const tagRender = (props) => {
    const { label, closable, onClose, value } = props;
    const onPreventMouseDown = (event) => {
      event.preventDefault();
      event.stopPropagation();
    };
    const idx = workspaceTags?.findIndex(t => t.value === value);
    const tagOption = workspaceTags?.[idx];
    const color = getTagColor(tagOption?.color) || getRandomColor(idx);
    return (
      <Tag
        color={color}
        onMouseDown={onPreventMouseDown}
        closable={closable}
        onClose={onClose}
        style={{ marginInlineEnd: 4 }}
        className='m-1 py-0.5 cursor-pointer'
      >
        {label}
      </Tag>
    );
  };

  return (
    <>
      <div className="w-full mt-5 mb-2">
        <div className="flex justify-between align-center">
          <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'rgba(15,15,25,0.35)' }}>
            <FormattedMessage id="TAGS" defaultMessage="Tags" />
          </div>
          {isOwnerOrAdmin && <div className="mr-4">
            <div
              className="flex items-center gap-1.5 px-2 py-0.5 rounded-lg cursor-pointer transition-colors"
              style={{
                background: 'rgba(99,102,241,0.07)',
                color: 'rgba(99,102,241,0.75)',
                border: '1px solid rgba(99,102,241,0.2)',
                fontSize: 12,
                fontWeight: 500,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.13)'; e.currentTarget.style.color = '#4f46e5'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.07)'; e.currentTarget.style.color = 'rgba(99,102,241,0.75)'; }}
              onClick={() => dispatch(toggleWorkspaceTagsModal(true))}
            >
              <FormattedMessage id="MANAGE_TAGS" defaultMessage="Manage Tags" />
            </div>
          </div>}
        </div>
      </div>
      <div className="w-full mb-1">
        <Select
          mode="multiple"
          style={{ width: '100%', minHeight: 40 }}
          tagRender={tagRender}
          placeholder="Group task to a workspace tag"
          onChange={(selectedTag) => {
            let tags = [];
            workspaceTags.map(tag => {
              if (selectedTag.includes(tag?.label)) {
                tags.push(tag.id);
              }
              return;
            });

            updateTaskTags(tags);
            setTaskTags(selectedTag);
          }}
          onSearch={(text) => setTagInput(text)}
          searchValue={tagInput}
          onClear={() => setTagInput('')}
          onDropdownVisibleChange={() => setTagInput('')}
          options={workspaceTags}
          value={taskTags}
          notFoundContent={(
            <div className="text-black hover:bg-slate-50 cursor-pointer">
              {tagInput ? (
                <div onClick={() => {
                  setTagInput('');
                  setTaskTags(taskTags => ([...taskTags, tagInput]));
                  dispatch(addWorkspaceTag({
                    tag: tagInput,
                    workspace: workspace,
                    metadata: {
                      color: getRandomColor(workspaceTags.length)
                    },
                  }, (tag) => {
                    let selectedTags = [];
                    const setectedTagsLabelList = taskTags;
                    workspaceTags.forEach(tag => {
                      if (setectedTagsLabelList.includes(tag?.label)) {
                        selectedTags.push(tag.id);
                      }
                    });
                    updateTaskTags([...(selectedTags || []), tag?._id])
                  }));
                }}>
                  Add <span className='text-blue-500'>{tagInput}</span> to tags?
                </div>
              ) : <FormattedMessage id="NO_TAGS_FOUND" defaultMessage="No tags found" />}
            </div>
          )}
        />
      </div>
    </>
  );
};

export default TaskTagsSection;
